import { useId, useState, type ChangeEvent } from 'react';
import { errorMessage } from '../api/client';
import type { IngestFinalizeRequest, RoomDocument } from '../api/types';
import { fileToBase64, oversizeMessage } from '../lib/file';
import { cn } from '../lib/utils';
import { Icon } from './icons';

/**
 * What a person can DO about a file we couldn't read (owner, 2026-10-15: "I
 * don't really have any options to view the doc to see or cure the
 * situation"). Shown under the "Couldn't read" note in the data-room row and in
 * the viewer, for the room admin and the bound seller alike:
 *
 *   - Open the file — the ORIGINAL in the in-app viewer (buyers never see it;
 *     their copy is unchanged);
 *   - Download — the original, through the content endpoint's own rules;
 *   - Upload a replacement — a new version of THIS document (the readable-copy
 *     path): the flag goes with the old version and the new one is read and
 *     filed like any upload;
 *   - It opens fine — clear the flag (the admin only) — a person's decision,
 *     audited; it withdraws the seller's request for a copy.
 *
 * The app passes its own mutations in (the shared kit holds no react-query).
 */
export interface UnreadableFileOptionsProps {
  document: Pick<RoomDocument, 'id' | 'name'>;
  /** Opens the original in the viewer. Omit where the viewer already shows it. */
  onOpen?: () => void;
  /** The original's download link (omit: no Download). */
  downloadHref?: string;
  /** Sends the picked file as a new version of this document. */
  onReplace: (body: IngestFinalizeRequest) => Promise<unknown>;
  /** "It opens fine — clear the flag": the room admin only (omit for the seller). */
  onClearFlag?: () => Promise<{ sellerRequestWithdrawn: boolean }>;
  className?: string;
}

const ACTION =
  'inline-flex min-h-6 items-center gap-1 rounded text-xs font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 max-md:min-h-tap';

type Note = { tone: 'ok' | 'error'; text: string };

export function UnreadableFileOptions({
  document,
  onOpen,
  downloadHref,
  onReplace,
  onClearFlag,
  className,
}: UnreadableFileOptionsProps) {
  const inputId = useId();
  const [uploading, setUploading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [note, setNote] = useState<Note | null>(null);

  async function onPick(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    const tooBig = oversizeMessage(file);
    if (tooBig) {
      setNote({ tone: 'error', text: tooBig });
      return;
    }
    setNote(null);
    setUploading(true);
    try {
      await onReplace({
        filename: file.name,
        contentType: file.type || 'application/octet-stream',
        contentBase64: await fileToBase64(file),
      });
      setNote({ tone: 'ok', text: 'Replacement uploaded — we’re reading it now.' });
    } catch (err) {
      setNote({ tone: 'error', text: errorMessage(err) ?? 'Upload failed. Please try again.' });
    } finally {
      setUploading(false);
    }
  }

  async function onClear() {
    if (!onClearFlag) return;
    setClearing(true);
    setNote(null);
    try {
      const res = await onClearFlag();
      setConfirming(false);
      setNote({
        tone: 'ok',
        text: res.sellerRequestWithdrawn
          ? 'Flag cleared — the seller is no longer asked for a copy.'
          : 'Flag cleared.',
      });
    } catch (err) {
      setNote({ tone: 'error', text: errorMessage(err) ?? 'Clearing the flag failed.' });
    } finally {
      setClearing(false);
    }
  }

  return (
    <div className={cn('mt-1 space-y-1', className)}>
      <div
        className="flex flex-wrap items-center gap-x-3 gap-y-1"
        role="group"
        aria-label={`What you can do about ${document.name}`}
      >
        {onOpen ? (
          <button type="button" className={ACTION} onClick={onOpen}>
            <Icon name="eye" size={12} />
            Open the file
          </button>
        ) : null}
        {downloadHref ? (
          <a className={ACTION} href={downloadHref} download={document.name}>
            <Icon name="download" size={12} />
            Download
          </a>
        ) : null}
        <input
          id={inputId}
          type="file"
          className="sr-only"
          onChange={(e) => void onPick(e)}
          disabled={uploading}
          aria-label={`Upload a replacement for ${document.name}`}
        />
        <label
          htmlFor={inputId}
          className={cn(ACTION, 'cursor-pointer focus-within:ring-2 focus-within:ring-ring')}
        >
          <Icon name="upload" size={12} />
          {uploading ? 'Uploading…' : 'Upload a replacement'}
        </label>
        {onClearFlag && !confirming ? (
          <button type="button" className={ACTION} onClick={() => setConfirming(true)}>
            <Icon name="check" size={12} />
            It opens fine — clear the flag
          </button>
        ) : null}
      </div>
      {onClearFlag && confirming ? (
        <div
          className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs"
          role="group"
          aria-label="Confirm clearing the flag"
        >
          <span className="text-ink-2">
            Clear the flag? The seller is no longer asked for a copy, and the file counts again.
          </span>
          <button
            type="button"
            className={cn(ACTION, 'font-semibold')}
            onClick={() => void onClear()}
            disabled={clearing}
          >
            {clearing ? 'Clearing…' : 'Clear the flag'}
          </button>
          <button
            type="button"
            className={cn(ACTION, 'text-ink-2')}
            onClick={() => setConfirming(false)}
            disabled={clearing}
          >
            Cancel
          </button>
        </div>
      ) : null}
      {note ? (
        <p
          role={note.tone === 'error' ? 'alert' : 'status'}
          className={cn(
            'text-xs',
            note.tone === 'error' ? 'text-destructive' : 'text-st-certified-fg',
          )}
        >
          {note.text}
        </p>
      ) : null}
    </div>
  );
}
